import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, InputNumber, Input, message, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

interface WorkflowStep {
  id?: string;
  step: number;
  step_number?: number;
  sort_order?: number;
  title: string;
  description: string;
}

interface WorkflowEditorProps {
  data: WorkflowStep[];
  onSave: (data: WorkflowStep[]) => void;
}

const WorkflowEditor: React.FC<WorkflowEditorProps> = ({ data, onSave }) => {
  const [items, setItems] = useState<WorkflowStep[]>(data || []);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    const normalized = (data || []).map((item, index) => {
      const stepNumber = item.step_number ?? item.step ?? index + 1;
      return {
        ...item,
        step: stepNumber,
        step_number: stepNumber,
        sort_order: item.sort_order ?? stepNumber,
      };
    });
    setItems(normalized);
  }, [data]);

  const doSave = (newItems: WorkflowStep[]) => {
    setItems(newItems);
    onSave(newItems.map((item, index) => {
      const stepNumber = item.step_number ?? item.step ?? index + 1;
      return {
        ...item,
        step: stepNumber,
        step_number: stepNumber,
        sort_order: item.sort_order ?? index + 1,
      };
    }));
  };

  const handleMove = (index: number, direction: 'up' | 'down') => {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    doSave(newItems.map((item, i) => ({
      ...item,
      step: i + 1,
      step_number: i + 1,
      sort_order: i + 1,
    })));
  };

  const columns = [
    {
      title: '步骤',
      dataIndex: 'step',
      key: 'step',
      width: 80,
      sorter: (a: WorkflowStep, b: WorkflowStep) => a.step - b.step,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => (
        <div dangerouslySetInnerHTML={{ __html: text.substring(0, 100) + (text.length > 100 ? '...' : '') }} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_: any, record: WorkflowStep, index: number) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={index === 0}
            onClick={() => handleMove(index, 'up')} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={index === items.length - 1}
            onClick={() => handleMove(index, 'down')} />
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(index)}>编辑</Button>
          <Popconfirm title="确定删除吗？" onConfirm={() => handleDelete(index)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    const item = items[index];
    form.setFieldsValue({
      step: item.step_number ?? item.step,
      title: item.title,
      description: item.description,
    });
    setModalVisible(true);
  };

  const handleDelete = (index: number) => {
    doSave(items.filter((_, i) => i !== index));
    message.success('删除成功');
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const newItem: WorkflowStep = {
        id: editingIndex !== null ? items[editingIndex]?.id : `workflow-${Date.now()}`,
        step: values.step,
        step_number: values.step,
        sort_order: editingIndex !== null ? items[editingIndex]?.sort_order ?? values.step : items.length + 1,
        title: values.title,
        description: values.description,
      };

      let newItems: WorkflowStep[];
      if (editingIndex !== null) {
        newItems = [...items];
        newItems[editingIndex] = newItem;
      } else {
        newItems = [...items, newItem];
      }
      doSave(newItems);
      setModalVisible(false);
      message.success(editingIndex !== null ? '更新成功' : '添加成功');
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          添加工作流程
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={items}
        rowKey={(_, index) => String(index)}
        pagination={false}
      />

      <Modal
        title={editingIndex !== null ? '编辑工作流程' : '添加工作流程'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="步骤号"
            name="step"
            rules={[{ required: true, message: '请输入步骤号' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="例如：1" />
          </Form.Item>

          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="例如：输入患者信息" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <Input.TextArea
              placeholder="支持 Markdown 格式&#10;例如：**粗体** *斜体* [链接](url)"
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkflowEditor;
